import Modal from "antd/es/modal";
import { BotType } from "@shared/types";
import Flex from "common/Boxes/Flex.tsx";
import { BOT_TYPE_TO_DISPLAY_NAME } from "@shared/features/bots/constants.ts";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title.tsx";
import {
  ColoredDcaBotIcon,
  ColoredGridBotIcon,
  ColoredManualTradingIcon,
  ColoredSignalsBotIcon,
  ColoredTradingViewBotIcon,
  DarkColoredSignalsBotIcon,
} from "common/Icons/Icons.tsx";
import Divider from "common/misc/Divider.tsx";
import { useHistory } from "react-router-dom";
import { BOTTOM_NAVIGATOR, CREATE_TRADE_PATH } from "common/constants.ts";
import { getCreateBotRoute } from "utils/routes.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import BottomDrawer from "common/Drawers/BottomDrawer.tsx";
import { getIsDarkMode } from "utils/misc.tsx";
import { useWindowSize } from "react-use";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

interface CreateBotModalProps {
  open: boolean;
  closeModal: () => void;
}

const CreateBotModal = ({ open, closeModal }: CreateBotModalProps) => {
  const theme = useTheme();
  const history = useHistory();
  const isDarkMode = getIsDarkMode();
  const { height } = useWindowSize();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const botTypeToIcon = {
    [BotType.SIGNAL]: isDarkMode
      ? DarkColoredSignalsBotIcon
      : ColoredSignalsBotIcon,
    [BotType.ASAP]: ColoredDcaBotIcon,
    [BotType.GRID]: ColoredGridBotIcon,
    [BotType.TRADING_VIEW]: ColoredTradingViewBotIcon,
  };
  const iconStyle = {
    width: isBottomNavigator ? 45 : 55,
    height: isBottomNavigator ? 28 : 38,
  };

  const getRow = (
    icon: JSX.Element,
    title: string,
    description: string,
    onClick?: () => void
  ) => {
    return (
      <RowWrapper onClick={onClick} $isDarkMode={isDarkMode} key={title}>
        <IconBg $isDarkMode={isDarkMode}>{icon}</IconBg>
        <div>
          <Title level={3}>{title}</Title>
          <Text type="medium">{description}</Text>
        </div>
      </RowWrapper>
    );
  };

  const handleClick = (botType?: BotType) => {
    if (!!botType) {
      history.push(getCreateBotRoute(botType));
    } else {
      history.push(CREATE_TRADE_PATH);
    }
    closeModal();
  };

  const title = "Create New Bot";
  const content = (
    <>
      {[BotType.SIGNAL, BotType.ASAP, BotType.GRID, BotType.TRADING_VIEW].map(
        (botType) => {
          const Icon = botTypeToIcon[botType];
          return getRow(
            <Icon style={iconStyle} />,
            `${BOT_TYPE_TO_DISPLAY_NAME[botType]} Bot`,
            BOT_TYPE_TO_DESCRIPTION[botType],
            () => handleClick(botType)
          );
        }
      )}
      <Divider text="Or" style={{ margin: isBottomNavigator ? 0 : "12px 0" }} />
      {getRow(
        <ColoredManualTradingIcon style={iconStyle} />,
        "Manual Trading",
        "Execute trades manually with precision and flexibility, utilizing Cornix’s powerful tools.",
        () => handleClick()
      )}
    </>
  );

  if (isBottomNavigator) {
    return (
      <BottomDrawer
        open={open}
        onClose={closeModal}
        title={title}
        content={content}
        contentStyle={{ maxHeight: height * 0.9, overflowY: "auto" }}
        rightButtons={
          <CloseOutlined
            onClick={closeModal}
            style={{ color: theme.colors.primaryGrey }}
          />
        }
      />
    );
  }
  return (
    <Modal
      title={title}
      open={open}
      onCancel={closeModal}
      footer={null}
      styles={{ body: { padding: 16 } }}
    >
      {content}
    </Modal>
  );
};

export default CreateBotModal;

const BOT_TYPE_TO_DESCRIPTION = {
  [BotType.SIGNAL]:
    "Follow signals from your favorite groups or our marketplace's experts, executing trades automatically.",
  [BotType.ASAP]:
    "Utilize dollar-cost averaging strategy with Cornix’s advanced features.",
  [BotType.GRID]:
    "Automatically place buy and sell orders at preset levels, profiting from market fluctuations.",
  [BotType.TRADING_VIEW]:
    "Link TradingView alerts to Cornix for automatic execution based on your strategies and indicators.",
};

const RowWrapper = styled(Flex)<{ $isDarkMode?: boolean }>`
  justify-content: flex-start;
  cursor: pointer;
  padding: 16px;
  @media (min-width: ${BOTTOM_NAVIGATOR}) {
    &:hover {
      background: ${(p) =>
        p.$isDarkMode ? "#374762" : p.theme.colors.lightBlue};
      border-radius: 12px;
    }
  }
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    padding: 16px 0;
    &:first-child {
      padding-top: 0;
    }
  }
`;
const IconBg = styled.div<{ $isDarkMode?: boolean }>`
  background: ${(p) => (p.$isDarkMode ? "#374762" : p.theme.colors.lightBlue)};
  border-radius: 15px;
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    width: 45px;
    height: 45px;
    border-radius: 12px;
  }
`;
